import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { Link, useSearchParams } from 'react-router-dom';
import PageHero from '../components/layout/PageHero';
import {
  NEWS_CATEGORIES,
  NewsCategory,
  newsCategoryLabelMap,
  normalizeNewsCategory,
  isNewsCategory,
} from '../constants/newsCategories';
import { compareDescByDate, formatChinaDate } from '../utils/date';
import MediaImage from '../components/MediaImage';
import { resolveMediaUrl } from '../utils/media';

type NewsItem = {
  _id: string;
  title: string;
  summary: string;
  category: NewsCategory;
  coverUrl?: string;
  publishedAt?: string;
  isFeatured?: boolean;
};

type NewsSidebarGallery = {
  title: string;
  images: string[];
};

const DEFAULT_INTRO =
  'Latest updates on activities, conferences, and graduation are presented here and highlighted on the homepage.';

const renderHtml = (html?: string) => ({ __html: html || '' });
const News: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intro, setIntro] = useState(DEFAULT_INTRO);
  const [activeCategory, setActiveCategory] = useState<NewsCategory | 'All'>('All');
  const [gallery, setGallery] = useState<NewsSidebarGallery>({
    title: 'Highlights',
    images: [],
  });
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);

  useEffect(() => {
    const category = searchParams.get('category');
    if (category && isNewsCategory(category)) {
      setActiveCategory(category);
    } else {
      setActiveCategory('All');
    }
  }, [searchParams]);

  const handleCategoryChange = (value: NewsCategory | 'All') => {
    setActiveCategory(value);
    if (value === 'All') {
      setSearchParams({});
    } else {
      setSearchParams({ category: value });
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<NewsItem[]>('/news');
        setItems(
          (res.data || []).map((item) => ({
            ...item,
            category: normalizeNewsCategory(item.category),
          }))
        );
      } catch (err) {
        console.error(err);
        setError('Failed to load news. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    api.get<{ news: string }>('/page-intros').then((res) => {
      const v = res.data?.news?.trim();
      if (v) setIntro(v);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get<NewsSidebarGallery>('/news-sidebar-gallery')
      .then((res) => {
        setGallery({
          title: res.data?.title || 'Highlights',
          images: Array.isArray(res.data?.images)
            ? res.data.images.map((u) => resolveMediaUrl(u) || u)
            : [],
        });
      })
      .catch(() => {
        setGallery({ title: 'Highlights', images: [] });
      });
  }, []);

  useEffect(() => {
    setActiveGalleryIndex(0);
  }, [gallery.images.length]);

  useEffect(() => {
    if (gallery.images.length <= 1) return () => {};
    const timer = window.setInterval(() => {
      setActiveGalleryIndex((prev) => (prev + 1) % gallery.images.length);
    }, 3500);
    return () => window.clearInterval(timer);
  }, [gallery.images.length]);

  const filteredItems = useMemo(
    () =>
      activeCategory === 'All'
        ? items
        : items.filter((item) => item.category === activeCategory),
    [items, activeCategory]
  );

  const displayItems = useMemo(
    () =>
      [...filteredItems].sort((a, b) => {
        const featuredDiff = Number(b.isFeatured) - Number(a.isFeatured);
        if (featuredDiff !== 0) return featuredDiff;
        return (
          compareDescByDate(a.publishedAt, b.publishedAt)
        );
      }),
    [filteredItems]
  );

  return (
    <section className="bg-surface min-h-full">
      <PageHero title="News" intro={intro} sectionLabel="Latest Updates" />
      <div className="mx-auto max-w-screen-2xl px-8 pb-20 pt-12">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,2fr)_360px]">
          <div className="space-y-8">
            <div className="flex flex-wrap gap-1 border-b-2 border-um-navy/70 pb-0">
              {[{ label: 'All', value: 'All' as const }, ...NEWS_CATEGORIES].map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => handleCategoryChange(tab.value)}
                  className={`um-nav-tab ${activeCategory === tab.value ? 'um-nav-tab-active' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {loading ? (
              <p className="text-on-surface-variant">Loading...</p>
            ) : error ? (
              <div className="rounded-md bg-error-container px-4 py-3 text-sm text-on-error-container">
                {error}
              </div>
            ) : filteredItems.length === 0 ? (
              <p className="text-on-surface-variant">No news in this category.</p>
            ) : (
              <div className="space-y-6">
                {displayItems.map((item) => (
                  <article
                    key={item._id}
                    className="um-card bg-surface-container-lowest p-7"
                  >
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <span className="rounded-full bg-secondary-container px-3 py-1 text-xs uppercase tracking-wider text-on-secondary-container">
                        {newsCategoryLabelMap[item.category || 'activity']}
                      </span>
                      <span className="text-xs text-on-surface-variant">
                        {item.publishedAt ? formatChinaDate(item.publishedAt) : ''}
                      </span>
                    </div>
                  <h2
                    className="text-3xl font-semibold leading-tight text-on-surface md:text-4xl"
                    dangerouslySetInnerHTML={renderHtml(item.title)}
                  />
                  <p
                    className="mt-4 text-base leading-relaxed text-on-surface-variant"
                    dangerouslySetInnerHTML={renderHtml(item.summary)}
                  />
                    <Link
                      to={`/news/${item._id}`}
                      className="um-btn-link mt-6"
                    >
                      Read more <span>→</span>
                    </Link>
                  </article>
                ))}
              </div>
            )}
          </div>

          <aside>
            <div className="um-card sticky top-28 overflow-hidden bg-surface-container-low">
              <div className="border-b border-outline-variant/30 px-5 py-4">
                <h3 className="text-lg font-semibold text-primary">
                  {gallery.title || 'Highlights'}
                </h3>
              </div>
              <div className="relative aspect-[4/3] w-full bg-surface-container">
                {gallery.images.length > 0 ? (
                  <MediaImage
                    src={gallery.images[activeGalleryIndex]}
                    alt={`news-gallery-${activeGalleryIndex + 1}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sm text-on-surface-variant">
                    No gallery images yet
                  </div>
                )}
              </div>
              {gallery.images.length > 1 && (
                <div className="flex items-center justify-center gap-2 px-4 py-3">
                  {gallery.images.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveGalleryIndex(idx)}
                      className={`h-2 w-2 rounded-full transition-colors ${
                        idx === activeGalleryIndex ? 'bg-primary' : 'bg-outline-variant'
                      }`}
                      aria-label={`Switch to image ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default News;

